import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import type { Invoice } from "$global-providers/rxdb-provider/collections/invoice";
import { useRxCollectionInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import useFacility from "$routes/_lib/use-facility";
import { BillingMode } from "elephant-web/gql";
import { useEffect, useState } from "react";

/**
 * Live list of the patient's invoices at the current facility, but only when
 * the facility runs PRE_SERVICE billing. Returns an empty array otherwise, so
 * POST_SERVICE screens neither query nor react. Updates as the cashier's
 * paid/void transitions replicate in — this is what makes payment badges
 * track payment without any bespoke propagation channel.
 */
export const usePreServiceInvoices = (patientId: string): Invoice[] => {
  const [rxdbBillingEnabled] = useFeatureFlag<boolean>(
    featureFlags.billing.rxdbBilling,
  );
  const { data: facility } = useFacility();
  const invoiceCollection = useRxCollectionInvoice();
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const facilityId = facility?.id;
  const isPreService = facility?.billingMode === BillingMode.PreService;

  useEffect(() => {
    if (
      !rxdbBillingEnabled ||
      !isPreService ||
      !invoiceCollection ||
      !facilityId ||
      !patientId
    ) {
      setInvoices([]);
      return;
    }

    const sub = invoiceCollection
      .find({ selector: { patientId, facilityId } })
      .$.subscribe((docs) => {
        setInvoices(docs.map((doc) => doc.toJSON() as Invoice));
      });
    return () => sub.unsubscribe();
  }, [
    rxdbBillingEnabled,
    isPreService,
    invoiceCollection,
    facilityId,
    patientId,
  ]);

  return invoices;
};
